/**
 * MyCourses (Sprint 4 B5) — page "Môn học của tôi".
 *
 * Layout:
 *   - Breadcrumb
 *   - Tabs filter: Tất cả / Đang học / Đã hoàn thành / Đã bỏ
 *   - Grid course card với status badge
 *   - Empty state cho từng tab
 */

import { useState } from "react";
import {
  Alert,
  Breadcrumb,
  Card,
  Col,
  Empty,
  Result,
  Row,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  BookOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useMyEnrollments, type EnrollmentStatus } from "@/api/enrollment";

const { Title, Text, Paragraph } = Typography;

type TabKey = "all" | EnrollmentStatus;

const STATUS_META: Record<
  EnrollmentStatus,
  { label: string; color: string; icon: React.ReactNode }
> = {
  enrolled: { label: "Đang học", color: "blue", icon: <ClockCircleOutlined /> },
  completed: { label: "Đã hoàn thành", color: "gold", icon: <CheckCircleOutlined /> },
  dropped: { label: "Đã bỏ", color: "default", icon: <StopOutlined /> },
  expired: { label: "Hết hạn", color: "red", icon: <StopOutlined /> },
};

export function MyCourses() {
  const [tab, setTab] = useState<TabKey>("all");
  const statusFilter = tab === "all" ? undefined : tab;
  const { data: enrollments, isLoading, error } = useMyEnrollments(statusFilter);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          {
            title: (
              <Link to="/">
                <HomeOutlined />
              </Link>
            ),
          },
          { title: "Môn học của tôi" },
        ]}
      />

      <Title level={2}>Môn học của tôi</Title>
      <Paragraph type="secondary">
        Quản lý các môn học bạn đã đăng ký, đang học và đã hoàn thành.
      </Paragraph>

      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as TabKey)}
        items={[
          { key: "all", label: "Tất cả" },
          { key: "enrolled", label: STATUS_META.enrolled.label },
          { key: "completed", label: STATUS_META.completed.label },
          { key: "dropped", label: STATUS_META.dropped.label },
        ]}
      />

      {isLoading && (
        <div style={{ textAlign: "center", padding: 64 }}>
          <Spin size="large" />
        </div>
      )}

      {error && <Result status="error" title="Không tải được danh sách" subTitle={String(error)} />}

      {!isLoading && enrollments && enrollments.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            tab === "all"
              ? "Bạn chưa đăng ký môn học nào. Hãy khám phá thư viện môn học."
              : `Không có môn học ${STATUS_META[tab as EnrollmentStatus].label.toLowerCase()}.`
          }
          style={{ padding: 48 }}
        >
          <Link to={tab === "all" ? "/study-plan" : "/courses"}>
            <Tag color="blue" style={{ fontSize: 14, padding: "6px 12px" }}>
              {tab === "all" ? "Kế hoạch học tập →" : "Khám phá môn học →"}
            </Tag>
          </Link>
        </Empty>
      )}

      {!isLoading && enrollments && enrollments.length > 0 && (
        <Row gutter={[16, 16]} data-testid="enrollments-grid">
          {enrollments.map((enr) => {
            const meta = STATUS_META[enr.status];
            return (
              <Col xs={24} sm={12} lg={8} key={enr.id}>
                <Link
                  to={`/courses/${enr.course.slug}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <Card
                    hoverable
                    cover={
                      enr.course.cover_image ? (
                        <img
                          src={enr.course.cover_image}
                          alt={enr.course.name}
                          style={{ height: 140, objectFit: "cover" }}
                        />
                      ) : (
                        <div
                          style={{
                            height: 140,
                            background: "linear-gradient(135deg,#1677ff,#5e8df5)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <BookOutlined style={{ fontSize: 36, color: "#fff" }} />
                        </div>
                      )
                    }
                  >
                    <Space direction="vertical" size={4} style={{ width: "100%" }}>
                      <Tag color="blue">{enr.course.code}</Tag>
                      <Text strong style={{ fontSize: 16 }}>
                        {enr.course.name}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {enr.course.term_names.length > 0
                          ? `${enr.course.term_names.join(", ")} · `
                          : ""}
                        {enr.course.lesson_count} bài
                      </Text>
                      <Tag icon={meta.icon} color={meta.color}>
                        {meta.label}
                      </Tag>
                    </Space>
                  </Card>
                </Link>
              </Col>
            );
          })}
        </Row>
      )}

      {!isLoading && !error && (!enrollments || enrollments.length === 0) && tab === "all" && (
        <Alert
          style={{ marginTop: 24 }}
          type="info"
          showIcon
          message="Tip: Đăng ký một môn học để mở khóa toàn bộ bài học (không chỉ bài preview)."
        />
      )}
    </div>
  );
}
